import React from "react";
import { Modal, Tooltip, Button, message, Segmented, Tag } from "antd";
import { malimaxThreeById } from "../../../../libs/apis";
import { MalimaxThreeI } from "../../../../libs/interfaces";
import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
  InfoCircleOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { OneCard } from "../../../../libs/shared";
import "../../../malimax-one/components/list/show-container.css";

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: "naviera" | "exporter" | "other" | "gps" | "additional";
}

interface ShowContainerPropsI {
  id: number;
  isError?: boolean;
}

export const ShowContainer = ({ id, isError }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) setOpen(true);
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer
          open={open}
          onClose={handleClose}
          id={id}
          isError={isError}
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────── */
/*  Helper: single info field                      */
/* ─────────────────────────────────────────────── */
const Field = ({
  label,
  value,
  full,
}: {
  label: string;
  value?: string | null;
  full?: boolean;
}) => (
  <div className={`info-block${full ? " full-width" : ""}`}>
    <div className="label">{label}</div>
    <div className={`value${!value ? " empty" : ""}`}>{value || "—"}</div>
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Form info panel                                */
/* ─────────────────────────────────────────────── */
const FormPanel = ({ data }: { data: MalimaxThreeI }) => (
  <div className="info-grid">
    {/* Tiempos */}
    <div className="time-row">
      <div className="time-badge">
        <span className="t-label">Hora inicio</span>
        <span className="t-value">{data.hourInit || "—"}</span>
      </div>
      <div className="time-badge">
        <span className="t-label">Hora fin</span>
        <span className="t-value">{data.hourEnd || "—"}</span>
      </div>
      <div className="time-badge">
        <span className="t-label">Guardado</span>
        <span className="t-value">
          {data.hourSaveUser
            ? data.hourSaveUser
            : data.timeStampSave
            ? new Date(data.timeStampSave as any).toLocaleTimeString()
            : "—"}
        </span>
      </div>
    </div>

    {/* General */}
    <div className="form-section-title">General</div>
    <Field label="Cliente" value={data.client} />
    <Field label="ID Cliente" value={data.clientIdentification} />
    <Field label="Puerto de entrada" value={data.entryPort} />
    <Field label="Coordenadas" value={data.coordinates} />
    <Field label="Coordenadas Panorámica" value={data.containerPanoramicCoordinates} />

    {/* Sellos / Videos comentarios */}
    <div className="form-section-title">Videos</div>
    <Field label="Comentario Video Puerta" value={data.doorVideoComment} />
    <Field label="Comentario Video Maquinaria" value={data.engineryVideoComment} />

    {/* Observaciones */}
    {data.observation && (
      <>
        <div className="form-section-title">Observaciones</div>
        <Field label="Observación" value={data.observation} full />
      </>
    )}
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Photo section helper                           */
/* ─────────────────────────────────────────────── */
const PhotoSection = ({
  title,
  type,
  cards,
  totalPhotos,
}: {
  title: string;
  type: OneCardI["type"];
  cards: OneCardI[];
  totalPhotos: string[];
}) => {
  const filtered = cards.filter((el) => el.type === type);
  if (!filtered.length) return null;
  return (
    <>
      <div className="section-header">
        <h3>{title}</h3>
      </div>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        {filtered.map((el) => (
          <OneCard data={el} key={el.title} lasFotos={totalPhotos} />
        ))}
      </Box>
    </>
  );
};

/* ─────────────────────────────────────────────── */
/*  Photos panel                                   */
/* ─────────────────────────────────────────────── */
const PhotosPanel = ({
  cards,
  totalPhotos,
}: {
  cards: OneCardI[];
  totalPhotos: string[];
}) => (
  <Box>
    <PhotoSection title="Naviera" type="naviera" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Exportador" type="exporter" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Otras" type="other" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="GPS" type="gps" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Fotos Adicionales" type="additional" cards={cards} totalPhotos={totalPhotos} />
  </Box>
);

/* ─────────────────────────────────────────────── */
/*  Modal                                          */
/* ─────────────────────────────────────────────── */
interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
  isError?: boolean;
}

export const ModalContainer = ({
  open,
  onClose,
  id,
  isError,
}: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<MalimaxThreeI>({
    container: "",
  } as MalimaxThreeI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);
  const [tab, setTab] = React.useState<string>("form");

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as MalimaxThreeI);
      setLoading(true);
      setTab("form");
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainerGet } = await malimaxThreeById(id);
      if (!dataContainerGet) return;
      const d = dataContainerGet;
      setData(d);

      const arrCards: OneCardI[] = [
        // Panorámica → naviera section (encabezado)
        { title: "Foto Panorámica",        photos: [{ src: d.containerPanoramicPhoto, alt: "Panorámica",       showAlt: true }], value: d.containerPanoramicComment, type: "naviera"  },
        // Naviera
        { title: "Foto Botella Naviera",   photos: [{ src: d.navieraBottlePhoto,      alt: "Botella Naviera",  showAlt: true }], value: d.navieraBottleComment,      type: "naviera"  },
        { title: "Foto Etiqueta Naviera",  photos: [{ src: d.navieraLabelPhoto,       alt: "Etiqueta Naviera", showAlt: true }], value: d.navieraLabelComment,       type: "naviera"  },
        { title: "Foto Cable Naviera",     photos: [{ src: d.navieraWirePhoto,        alt: "Cable Naviera",    showAlt: true }], value: d.navieraWireComment,        type: "naviera"  },
        // Exportador
        { title: "Foto Botella Exportador",photos: [{ src: d.exporterBottlePhoto,     alt: "Botella Exportador",showAlt: true }],value: d.exporterBottleComment,     type: "exporter" },
        { title: "Foto Etiqueta Exportador",photos:[{ src: d.exporterLabelPhoto,      alt: "Etiqueta Exportador",showAlt:true }],value: d.exporterLabelComment,      type: "exporter" },
        { title: "Foto Cable Exportador",  photos: [{ src: d.exporterWirePhoto,       alt: "Cable Exportador", showAlt: true }], value: d.exporterWireComment,       type: "exporter" },
        // Otro
        { title: "Foto Botella Otro",      photos: [{ src: d.otherBottlePhoto,        alt: "Botella Otro",     showAlt: true }], value: d.otherBottleComment,        type: "other"    },
        { title: "Foto Etiqueta Otro",     photos: [{ src: d.otherLabelPhoto,         alt: "Etiqueta Otro",    showAlt: true }], value: d.otherLabelComment,         type: "other"    },
        { title: "Foto Cable Otro",        photos: [{ src: d.otherWirePhoto,          alt: "Cable Otro",       showAlt: true }], value: d.otherWireComment,          type: "other"    },
        // GPS
        { title: "Foto GPS",               photos: [{ src: d.gpsPhoto,               alt: "GPS",              showAlt: true }], value: d.gpsComment,                type: "gps"      },
        { title: "Foto Sello GPS",         photos: [{ src: d.gpsStampPhoto,          alt: "Sello GPS",        showAlt: true }], value: d.gpsStampComment,           type: "gps"      },
      ];

      (d.images ?? []).forEach((img, i) => {
        if (!img?.src) return;
        arrCards.push({
          title: `Foto Adicional ${i + 1}`,
          photos: [{ src: img.src, alt: "Adicional", showAlt: true }],
          value: img.comment ?? "",
          type: "additional",
        });
      });

      const lasFotos: string[] = [];
      arrCards.forEach((card) =>
        card.photos.forEach((p) => lasFotos.push(p.src as string))
      );

      setTotalPhotos(lasFotos);
      setCards(arrCards);
      setLoading(false);
    } catch (err: any) {
      message.error((err as Error).message);
      setLoading(false);
    }
  };

  const photoCount = cards.filter((c) => c.photos?.[0]?.src).length;

  return (
    <Modal
      title={
        <p className="modal-header-gradient">
          {`CONTENEDOR N° ${data.container}`}
          {data.statusWorkflow && (
            <Tag
              color={data.statusWorkflowColor || "#25D228"}
              style={{ marginLeft: 10, fontSize: "0.75rem", verticalAlign: "middle" }}
            >
              {data.statusWorkflow}
            </Tag>
          )}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{ background: "red", color: "white", padding: "5px", borderRadius: "5px" }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={820}
      centered
      zIndex={1602}
      destroyOnClose
      style={{ padding: 0 }}
    >
      {/* Segmented control */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, mt: 0.5 }}>
        <Segmented
          className="modal-segmented"
          value={tab}
          onChange={(v) => setTab(v as string)}
          options={[
            {
              label: (
                <span>
                  <InfoCircleOutlined style={{ marginRight: 5 }} />
                  Información
                </span>
              ),
              value: "form",
            },
            {
              label: (
                <span>
                  <PictureOutlined style={{ marginRight: 5 }} />
                  Fotos
                  {photoCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#25D228",
                        color: "white",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: "0.7rem",
                      }}
                    >
                      {photoCount}
                    </span>
                  )}
                </span>
              ),
              value: "photos",
            },
          ]}
          style={{ fontWeight: 600 }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ overflowY: "auto", maxHeight: 520, p: 1.5 }}>
        {tab === "form" ? (
          <FormPanel data={data} />
        ) : (
          <PhotosPanel cards={cards} totalPhotos={totalPhotos} />
        )}
      </Box>
    </Modal>
  );
};